import { useState } from 'react';
import type { EventCategory } from '@/lib/types';
import { ALL_CATEGORIES, CATEGORY_LABELS, TAG_COLORS } from '@/lib/constants';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/useToast';

interface TagFilterChipsProps {
  selected: EventCategory[];
  onChange: (selected: EventCategory[]) => void;
}

export function TagFilterChips({ selected, onChange }: TagFilterChipsProps) {
  const { toast } = useToast();
  const [shakeId, setShakeId] = useState<EventCategory | null>(null);

  const shake = (cat: EventCategory) => {
    setShakeId(cat);
    setTimeout(() => setShakeId(null), 600);
    toast('Keep at least one category active', 'error');
  };

  const toggle = (cat: EventCategory) => {
    if (cat === 'all') {
      if (selected.includes('all')) {
        // Remove 'all' from selected
        const next = selected.filter((c) => c !== 'all');
        if (next.length === 0) {
          shake('all');
          return;
        }
        onChange(next);
      } else {
        // Select all categories
        onChange([...ALL_CATEGORIES]);
      }
      return;
    }

    if (selected.includes(cat)) {
      const next = selected.filter((c) => c !== cat);
      if (next.length === 0) {
        shake(cat);
        return;
      }
      onChange(next);
    } else {
      onChange([...selected, cat]);
    }
  };

  return (
    <div className="flex flex-wrap gap-2">
      {ALL_CATEGORIES.map((cat) => {
        const isSelected = selected.includes(cat);
        const colors = TAG_COLORS[cat];
        return (
          <button
            key={cat}
            onClick={() => toggle(cat)}
            className={cn(
              'px-3 h-9 rounded-full text-sm font-medium border transition-all duration-150 cursor-pointer',
              shakeId === cat && 'animate-shake'
            )}
            style={
              isSelected
                ? {
                    background: colors.bg,
                    color: colors.text,
                    border: `1px solid ${colors.border}`,
                  }
                : {
                    background: 'rgba(255,255,255,0.05)',
                    color: 'rgba(255,255,255,0.4)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }
            }
          >
            {CATEGORY_LABELS[cat]}
          </button>
        );
      })}
    </div>
  );
}
